// /catalog/items/new  — crear item nuevo (pide tipo + form dinamico).
// /catalog/items/:favoriteId — editar item existente (form dinamico + huerfanos).

import { useMemo, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AdminApiError,
  createFavoriteItem,
  getFavoriteItem,
  listFavoriteTypes,
  patchFavoriteItem,
  type FavoriteItem,
  type FavoriteType,
} from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { useCatalogContext } from '@/components/CatalogShell';
import DynamicForm, {
  findOrphanKeys,
  readTypeParameters,
  type DynamicFormValues,
} from '@/components/DynamicForm';
import { readFormSchema } from '@/components/FormSchemaEditor';
import { fieldCls, checkboxCls } from '@/lib/form-styles';
import { canWrite as canWriteSection } from '@/lib/access';

interface FormState {
  favorite_type_id: string;
  code: string;
  name: string;
  description: string;
  typeParameters: DynamicFormValues;
}

function initialState(): FormState {
  return { favorite_type_id: '', code: '', name: '', description: '', typeParameters: {} };
}

function fromItem(item: FavoriteItem): FormState {
  return {
    favorite_type_id: item.favorite_type_id,
    code: item.code,
    name: item.name,
    description: item.description ?? '',
    typeParameters: readTypeParameters(item.details),
  };
}

function buildDetails(
  state: FormState,
  base: Record<string, unknown> = {},
  schemaKeys: Set<string>,
  keepOrphans: boolean,
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...base };
  const params: DynamicFormValues = {};
  // Solo persistimos keys conocidas del schema. Si keepOrphans=true, ademas
  // preservamos las keys huerfanas con su valor actual.
  for (const [k, v] of Object.entries(state.typeParameters)) {
    if (schemaKeys.has(k) || keepOrphans) params[k] = v;
  }
  next['type_parameters'] = params;
  return next;
}

export default function CatalogItemEdit(): JSX.Element {
  const { favoriteId } = useParams<{ favoriteId: string }>();
  const isNew = !favoriteId || favoriteId === 'new';
  const { partnerId } = useCatalogContext();
  const session = useSession();
  const canWrite = canWriteSection(session, 'catalog');
  const navigate = useNavigate();
  const qc = useQueryClient();

  const typesQ = useQuery({
    queryKey: ['admin-favorite-types', partnerId],
    queryFn: () => listFavoriteTypes(partnerId),
    enabled: !!partnerId,
  });

  const itemQ = useQuery({
    queryKey: ['admin-favorite-item', favoriteId],
    queryFn: () => getFavoriteItem(favoriteId!),
    enabled: !isNew && !!favoriteId,
  });

  const [form, setForm] = useState<FormState>(initialState);
  const [hydrated, setHydrated] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [typeChangeAck, setTypeChangeAck] = useState(false);

  if (!isNew && itemQ.data && !hydrated) {
    setForm(fromItem(itemQ.data));
    setHydrated(true);
  }

  const activeTypes = useMemo(
    () => (typesQ.data ?? []).filter((t) => t.status === 'active'),
    [typesQ.data],
  );

  const selectedType: FavoriteType | undefined = useMemo(
    () => (typesQ.data ?? []).find((t) => t.favorite_type_id === form.favorite_type_id),
    [typesQ.data, form.favorite_type_id],
  );

  const schema = useMemo(() => readFormSchema(selectedType?.details), [selectedType]);
  const schemaKeys = useMemo(() => new Set(schema.fields.map((f) => f.key)), [schema]);
  const orphanKeys = useMemo(
    () => findOrphanKeys(schema, form.typeParameters),
    [schema, form.typeParameters],
  );

  const originalTypeId = itemQ.data?.favorite_type_id ?? '';
  const typeChanged = !isNew && form.favorite_type_id !== originalTypeId && originalTypeId !== '';

  const createMut = useMutation({
    mutationFn: async () => {
      const res = await createFavoriteItem(partnerId, {
        favorite_type_id: form.favorite_type_id,
        code: form.code.trim(),
        name: form.name.trim(),
        description: form.description.trim() || null,
        details: buildDetails(form, {}, schemaKeys, false),
      });
      return res.favorite_id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ['admin-favorite-items', partnerId] });
      navigate(`/catalog/items/${encodeURIComponent(id)}`);
    },
    onError: (e) =>
      setFormError(e instanceof AdminApiError ? e.message : 'No pudimos crear el item.'),
  });

  const patchMut = useMutation({
    mutationFn: async () => {
      if (!itemQ.data) throw new Error('not loaded');
      await patchFavoriteItem(itemQ.data.favorite_id, {
        favorite_type_id: form.favorite_type_id,
        name: form.name.trim(),
        description: form.description.trim() || null,
        // Preservamos orphans existentes (decision: no auto-purgar data).
        details: buildDetails(form, itemQ.data.details, schemaKeys, true),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-favorite-items', partnerId] });
      qc.invalidateQueries({ queryKey: ['admin-favorite-item', favoriteId] });
    },
    onError: (e) =>
      setFormError(e instanceof AdminApiError ? e.message : 'No pudimos guardar el item.'),
  });

  function onSubmit(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setFormError(null);
    if (!form.favorite_type_id) {
      setFormError('Elegi un tipo.');
      return;
    }
    if (typeChanged && !typeChangeAck) {
      setFormError('Confirma el cambio de tipo (checkbox de advertencia) antes de guardar.');
      return;
    }
    if (isNew) {
      if (!/^[a-z0-9_-]+$/.test(form.code)) {
        setFormError('Code invalido. Usa solo a-z, 0-9, _ o -.');
        return;
      }
      createMut.mutate();
    } else {
      patchMut.mutate();
    }
  }

  function clearOrphan(key: string): void {
    if (!canWrite) return;
    const next = { ...form.typeParameters };
    delete next[key];
    setForm({ ...form, typeParameters: next });
  }

  if (typesQ.isLoading || (!isNew && itemQ.isLoading)) {
    return <p className="text-sm text-ink-muted">Cargando…</p>;
  }
  if (!isNew && itemQ.isError) {
    return (
      <div role="alert" className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error">
        No pudimos cargar el item.{' '}
        <Link to="/catalog/items" className="underline">Volver</Link>
      </div>
    );
  }
  if (activeTypes.length === 0) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-ink-muted">
          No hay tipos activos para este partner. Crea uno primero desde la pestaña{' '}
          <Link to="/catalog/types" className="underline">Tipos</Link>.
        </p>
      </div>
    );
  }

  const isPending = createMut.isPending || patchMut.isPending;

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">
            {isNew ? 'Nuevo item' : `Editar: ${itemQ.data?.name}`}
          </h2>
          {!isNew && itemQ.data ? (
            <p className="text-xs font-mono text-ink-muted">{itemQ.data.favorite_id}</p>
          ) : null}
        </div>
        <Link to="/catalog/items" className="inline-flex items-center gap-1 text-sm text-ink-muted hover:underline">
          <ArrowLeft size={14} aria-hidden="true" />
          Volver a la lista
        </Link>
      </header>

      <section className="bg-surface dark:bg-surface-dark rounded-xl border border-surface-border dark:border-surface-dark-border p-5 space-y-4">
        <h3 className="text-sm font-semibold">Metadata</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-sm space-y-1 md:col-span-2">
            <span className="font-medium">Tipo</span>
            <select
              value={form.favorite_type_id}
              onChange={(e) => setForm({ ...form, favorite_type_id: e.target.value })}
              disabled={!canWrite}
              required
              className={fieldCls({ size: 'lg' })}
            >
              <option value="">— Elegi un tipo —</option>
              {activeTypes.map((t) => (
                <option key={t.favorite_type_id} value={t.favorite_type_id}>
                  {t.name} ({t.code})
                </option>
              ))}
            </select>
          </label>

          {typeChanged ? (
            <div role="alert" className="md:col-span-2 rounded-lg border border-status-warning/30 bg-status-warning/10 p-3 text-sm text-status-warning space-y-2">
              <p>
                <strong>Estas cambiando el tipo de un item existente.</strong> Esto
                puede invalidar los <code>type_parameters</code> guardados: los
                campos del nuevo tipo se piden vacios y los datos del tipo anterior
                quedan como "campos no reconocidos" hasta que los limpies.
              </p>
              <label className="text-sm flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={typeChangeAck}
                  onChange={(e) => setTypeChangeAck(e.target.checked)}
                  className={checkboxCls()}
                />
                Entiendo el efecto en cascada y quiero continuar.
              </label>
            </div>
          ) : null}

          <label className="text-sm space-y-1">
            <span className="font-medium">Code <span className="text-ink-muted">(slug)</span></span>
            <input
              type="text"
              value={form.code}
              onChange={(e) =>
                setForm({ ...form, code: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') })
              }
              disabled={!isNew || !canWrite}
              required
              maxLength={64}
              placeholder="river-plate"
              className={fieldCls({ size: 'lg', mono: true })}
            />
            {!isNew ? (
              <span className="text-xs text-ink-muted">No editable. Crear un item nuevo para renombrar.</span>
            ) : null}
          </label>
          <label className="text-sm space-y-1">
            <span className="font-medium">Nombre</span>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              disabled={!canWrite}
              required
              maxLength={200}
              className={fieldCls({ size: 'lg' })}
            />
          </label>
          <label className="text-sm space-y-1 md:col-span-2">
            <span className="font-medium">Descripcion <span className="text-ink-muted">(opcional)</span></span>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              disabled={!canWrite}
              maxLength={2000}
              rows={2}
              className={fieldCls({ size: 'lg' })}
            />
          </label>
        </div>
      </section>

      {selectedType ? (
        <section className="bg-surface dark:bg-surface-dark rounded-xl border border-surface-border dark:border-surface-dark-border p-5 space-y-4">
          <div>
            <h3 className="text-sm font-semibold">Campos del tipo {selectedType.name}</h3>
            <p className="text-xs text-ink-muted mt-1">
              Definidos en el form_schema de este tipo. Se guardan en
              <code className="mx-1">details.type_parameters</code>.
            </p>
          </div>
          <DynamicForm
            schema={schema}
            values={form.typeParameters}
            onChange={(values) => setForm({ ...form, typeParameters: values })}
            disabled={!canWrite}
          />

          {orphanKeys.length > 0 ? (
            <details className="rounded-lg border border-status-warning/30 bg-status-warning/10 p-3 text-sm">
              <summary className="cursor-pointer font-medium text-status-warning">
                Campos no reconocidos ({orphanKeys.length})
              </summary>
              <p className="text-xs text-ink-muted mt-2">
                Valores guardados con un schema anterior. No se descartan automaticamente —
                podes limpiarlos manualmente.
              </p>
              <ul className="mt-2 space-y-1">
                {orphanKeys.map((k) => (
                  <li key={k} className="flex items-center gap-2 text-xs">
                    <code className="font-mono">{k}</code>
                    <span className="text-ink-muted">
                      = {JSON.stringify(form.typeParameters[k])}
                    </span>
                    <button
                      type="button"
                      onClick={() => clearOrphan(k)}
                      disabled={!canWrite}
                      className="text-status-error hover:underline disabled:opacity-50"
                    >
                      limpiar
                    </button>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </section>
      ) : null}

      {formError ? (
        <div role="alert" className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error">
          {formError}
        </div>
      ) : null}

      <div className="flex justify-end gap-2">
        <Link
          to="/catalog/items"
          className="min-h-[2.75rem] inline-flex items-center rounded-lg border border-surface-border dark:border-surface-dark-border px-4 text-sm"
        >
          Cancelar
        </Link>
        <button
          type="submit"
          disabled={!canWrite || isPending}
          className="min-h-[2.75rem] rounded-lg bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 disabled:opacity-50"
        >
          {isPending ? 'Guardando…' : isNew ? 'Crear item' : 'Guardar cambios'}
        </button>
      </div>
    </form>
  );
}
